/* eslint-disable react/prop-types */
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Components & Services
import { JournalVoucher8Service } from "services/journal-voucher-8";
import { CompanyService } from "services/companyService";
import { getPrintTemplate } from "./components/printTemplate";
import { formatVndIntegerValue } from "utils";

const fmt = (n) => (n && n !== 0 ? formatVndIntegerValue(n) : "");

function JournalVoucher8Detail() {
  const { id } = useParams();
  const navigation = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [companyInfo, setCompanyInfo] = useState({ name: "", address: "" });

  useEffect(() => {
    if (id) {
      (async () => {
        try {
          const response = await JournalVoucher8Service.getById(id);
          const apiData = response.data || response;

          // Parse reportDate
          let sDay = initialValues.signingDay;
          let sMonth = initialValues.signingMonth;
          let sYear = initialValues.signingYear;
          if (apiData.reportDate && apiData.reportDate.includes("/")) {
            const parts = apiData.reportDate.split("/");
            if (parts.length === 3) {
              [sDay, sMonth, sYear] = parts;
            }
          }

          setFormInitials({
            ...apiData,
            signingDay: sDay,
            signingMonth: sMonth,
            signingYear: sYear,
          });
        } catch (error) {
          console.error("Lỗi load S04a8-DN:", error);
          toast.error("Không thể tải dữ liệu");
        }
      })();
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleBack = () => navigation("/templates/journal-voucher-8");

  const handlePrint = () => {
    if (!data) return;
    const printData = {
      ...data,
      unit: companyInfo.name,
      address: companyInfo.address,
      signingDay: data.reportDate?.split("/")[0] || "",
      signingMonth: data.reportDate?.split("/")[1] || "",
      signingYear: data.reportDate?.split("/")[2] || "",
    };
    const html = getPrintTemplate(printData);
    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(`
      <html>
        <head>
          <title>In Nhật ký chứng từ số 8 S04a8-DN</title>
          <style>@media print { @page { size: A4 landscape; margin: 1cm; } }</style>
        </head>
        <body onload="setTimeout(() => [window.print(), window.close()], 500)">${html}</body>
      </html>
    `);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox py={3} textAlign="center">
          <SoftTypography>Đang tải dữ liệu...</SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (!data) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox py={3} textAlign="center">
          <SoftTypography color="error">Không tìm thấy dữ liệu</SoftTypography>
          <SoftButton
            variant="gradient"
            color="light"
            onClick={handleBack}
            sx={{ mt: 2 }}
          >
            Trở về
          </SoftButton>
        </SoftBox>
      </DashboardLayout>
    );
  }

  const s = {
    container: {
      fontFamily: '"Times New Roman", Times, serif',
      color: "#000",
      padding: "20px",
      backgroundColor: "#fff",
      fontSize: "12px",
      lineHeight: "1.3",
    },
    th: {
      border: "1px solid #000",
      padding: "4px",
      textAlign: "center",
      fontWeight: "bold",
      verticalAlign: "middle",
      backgroundColor: "#f2f2f2",
    },
    td: { border: "1px solid #000", padding: "4px", height: "28px" },
    textRight: { textAlign: "right" },
    textCenter: { textAlign: "center" },
    bold: { fontWeight: "bold" },
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={10}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Card>
              <SoftBox p={3}>
                <div style={s.container}>
                  {/* Header */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "10px",
                    }}
                  >
                    <div style={{ width: "40%" }}>
                      <div style={{ fontWeight: "bold" }}>
                        Đơn vị: {companyInfo.name}
                      </div>
                      <div style={{ fontWeight: "bold", marginTop: 5 }}>
                        Địa chỉ: {companyInfo.address}
                      </div>
                    </div>
                    <div style={{ textAlign: "center", width: "60%" }}>
                      <div style={{ fontWeight: "bold", fontSize: "14px" }}>
                        Mẫu số S04a8-DN
                      </div>
                      <div style={{ fontStyle: "italic", fontSize: "10px" }}>
                        (Kèm theo Thông tư số 99/2025/TT-BTC <br /> ngày 27
                        tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)
                      </div>
                    </div>
                  </div>

                  {/* Title */}
                  <div style={{ textAlign: "center", margin: "20px 0" }}>
                    <h2
                      style={{
                        margin: 0,
                        textTransform: "uppercase",
                        fontSize: "18px",
                      }}
                    >
                      NHẬT KÝ CHỨNG TỪ SỐ 8
                    </h2>
                    <div
                      style={{
                        fontWeight: "bold",
                        fontSize: "14px",
                        marginTop: 5,
                      }}
                    >
                      Ghi Có các TK: 155, 156, 157, 158, 131, 511, 515, 521,
                      632, 635, 641, 642, 711, 811, 821, 911
                    </div>
                    <div style={{ marginTop: 10, fontWeight: "bold" }}>
                      Tháng {data.month} năm {data.year}
                    </div>
                  </div>

                  {/* Table */}
                  <div style={{ overflowX: "auto", border: "1px solid #000" }}>
                    <table
                      style={{
                        borderCollapse: "collapse",
                        minWidth: "1600px",
                        width: "100%",
                      }}
                    >
                      <thead>
                        <tr>
                          <th style={{ ...s.th, width: "50px" }} rowSpan={2}>
                            Số TT
                          </th>
                          <th style={{ ...s.th, width: "80px" }} rowSpan={2}>
                            Số hiệu TK ghi Nợ
                          </th>
                          <th style={{ ...s.th, width: "250px" }} rowSpan={2}>
                            Các TK ghi Nợ
                          </th>
                          <th style={s.th} colSpan={16}>
                            Các TK ghi Có
                          </th>
                          <th style={{ ...s.th, width: "120px" }} rowSpan={2}>
                            Cộng
                          </th>
                        </tr>
                        <tr>
                          {data.creditAccountLabels?.map((label, idx) => (
                            <th key={idx} style={{ ...s.th, width: "80px" }}>
                              {label || "..."}
                            </th>
                          ))}
                        </tr>
                        <tr style={{ backgroundColor: "#eee" }}>
                          <th style={s.th}>A</th>
                          <th style={s.th}>B</th>
                          <th style={s.th}>C</th>
                          {[...Array(16)].map((_, i) => (
                            <th key={i} style={s.th}>
                              {i + 1}
                            </th>
                          ))}
                          <th style={s.th}>17</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.rows?.map((row, idx) => (
                          <tr key={idx}>
                            <td style={{ ...s.td, ...s.textCenter }}>
                              {row.index}
                            </td>
                            <td style={{ ...s.td, ...s.textCenter }}>
                              {row.debitAccountCode}
                            </td>
                            <td style={s.td}>{row.debitAccountName}</td>
                            {row.creditValues?.map((val, cIdx) => (
                              <td
                                key={cIdx}
                                style={{ ...s.td, ...s.textRight }}
                              >
                                {fmt(val)}
                              </td>
                            ))}
                            <td
                              style={{
                                ...s.td,
                                ...s.textRight,
                                ...s.bold,
                                backgroundColor: "#f9f9f9",
                              }}
                            >
                              {fmt(row.rowTotal)}
                            </td>
                          </tr>
                        ))}
                        {/* Totals */}
                        <tr
                          style={{
                            backgroundColor: "#fafafa",
                            fontWeight: "bold",
                          }}
                        >
                          <td style={s.th} colSpan={3}>
                            Cộng
                          </td>
                          {data.totals?.creditValues?.map((v, i) => (
                            <td key={i} style={{ ...s.td, ...s.textRight }}>
                              {fmt(v)}
                            </td>
                          ))}
                          <td style={{ ...s.td, ...s.textRight }}>
                            {fmt(data.totals?.grandTotal)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Footer Info */}
                  <div style={{ marginTop: "20px" }}>
                    Đã ghi sổ cái ngày{" "}
                    <span style={s.bold}>
                      {data.recordedToLedgerDay || "..."}
                    </span>{" "}
                    tháng{" "}
                    <span style={s.bold}>
                      {data.recordedToLedgerMonth || "..."}
                    </span>{" "}
                    năm{" "}
                    <span style={s.bold}>
                      {data.recordedToLedgerYear || "..."}
                    </span>
                  </div>

                  {/* Signatures */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: "40px",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ width: "30%" }}>
                      <div style={{ fontWeight: "bold" }}>Người ghi sổ</div>
                      <div style={{ fontStyle: "italic", fontSize: "11px" }}>
                        (Ký, họ tên)
                      </div>
                      <SoftBox mt={8}>
                        <span style={s.bold}>
                          {data.ledgerClerk || "...................."}
                        </span>
                      </SoftBox>
                    </div>
                    <div style={{ width: "30%" }}>
                      <div style={{ fontWeight: "bold" }}>Kế toán trưởng</div>
                      <div style={{ fontStyle: "italic", fontSize: "11px" }}>
                        (Ký, họ tên)
                      </div>
                      <SoftBox mt={8}>
                        <span style={s.bold}>
                          {data.chiefAccountant || "...................."}
                        </span>
                      </SoftBox>
                    </div>
                    <div style={{ width: "40%" }}>
                      <div
                        style={{
                          fontStyle: "italic",
                          fontSize: "12px",
                          marginBottom: "3px",
                        }}
                      >
                        Ngày {data.reportDate?.split("/")[0] || "..."} tháng{" "}
                        {data.reportDate?.split("/")[1] || "..."} năm{" "}
                        {data.reportDate?.split("/")[2] || "..."}
                      </div>
                      <div style={{ fontWeight: "bold" }}>
                        Người đại diện theo pháp luật
                      </div>
                      <div style={{ fontStyle: "italic", fontSize: "11px" }}>
                        (Ký, họ tên, đóng dấu)
                      </div>
                      <SoftBox mt={8}>
                        <span style={s.bold}>
                          {data.legalRepresentative || "...................."}
                        </span>
                      </SoftBox>
                    </div>
                  </div>
                </div>

                <SoftBox display="flex" justifyContent="space-between" mt={4}>
                  <SoftButton
                    variant="gradient"
                    color="light"
                    onClick={handleBack}
                  >
                    Trở về
                  </SoftButton>
                  <SoftButton
                    variant="gradient"
                    color="success"
                    onClick={handlePrint}
                  >
                    In Nhật ký S04a8-DN
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default JournalVoucher8Detail;
